import { ActionIconButton } from '@/shared/components/ui/ActionIconButton';
import { Button } from '@/shared/components/ui/Button';
import { useClientPagination } from '@/shared/hooks/useClientPagination';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { confirm } from '@/shared/lib/confirm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import {
  type CustomFieldDef,
  createCustomFieldDef,
  deleteCustomFieldDef,
  listCustomFieldDefs,
  updateCustomFieldDef,
} from '../api';

export function CustomFieldsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['custom-fields'], queryFn: listCustomFieldDefs });
  const [editing, setEditing] = useState<CustomFieldDef | 'new' | null>(null);
  const all = q.data ?? [];
  const { visible, hasMore, sentinelRef } = useClientPagination(all);
  const delMut = useMutation({
    mutationFn: deleteCustomFieldDef,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['custom-fields'] }),
  });

  return (
    <section className="flex h-full flex-col overflow-hidden">
      <header className="mb-5 flex flex-shrink-0 items-center justify-between">
        <div>
          <h2
            className="font-bold text-[20px] text-n-900"
            style={{ letterSpacing: '-.02em', marginBottom: '4px' }}
          >
            Campos customizados
          </h2>
          <p className="text-[13px] text-n-500">
            Estendem o cadastro de contatos. Disponível em forms, importação e Mustache.
          </p>
        </div>
        <Button onClick={() => setEditing('new')}>
          <Plus />
          Novo campo
        </Button>
      </header>

      {!q.isLoading && all.length > 0 && (
        <p className="mb-2 flex-shrink-0 text-[12px] text-n-500">
          {visible.length === all.length
            ? `${all.length} ${all.length === 1 ? 'campo' : 'campos'}`
            : `Mostrando ${visible.length} de ${all.length} campos`}
        </p>
      )}
      <div className="min-h-0 flex flex-col overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Key
              </th>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Label
              </th>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Tipo
              </th>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Obrigatório
              </th>
              <th className="w-[180px] px-[14px] py-[10px] text-right text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center">
                  <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!q.isLoading && all.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-n-500">
                  Nenhum campo customizado ainda.
                </td>
              </tr>
            )}
            {visible.map((c) => (
              <tr key={c.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                <td className="px-[14px] py-[11px] text-[12px] text-n-700">{c.key}</td>
                <td className="px-[14px] py-[11px] font-medium text-n-900">{c.label}</td>
                <td className="px-[14px] py-[11px] text-[12px] capitalize text-n-700">{c.type}</td>
                <td className="px-[14px] py-[11px] text-[12px]">{c.required ? 'Sim' : 'Não'}</td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex justify-end gap-1">
                    <ActionIconButton
                      icon={Pencil}
                      label="Editar"
                      tone="brand"
                      onClick={() => setEditing(c)}
                    />
                    <ActionIconButton
                      icon={Trash2}
                      label="Apagar"
                      tone="danger"
                      onClick={async () => {
                        const ok = await confirm({
                          title: `Apagar "${c.label}"?`,
                          description: 'Valores em contatos serão preservados.',
                          confirmText: 'Apagar',
                          variant: 'danger',
                        });
                        if (ok) delMut.mutate(c.id);
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {hasMore && (
              <tr ref={sentinelRef}>
                <td colSpan={5} className="py-4 text-center text-[12px] text-n-500">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Carregando mais…
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <CustomFieldFormModal
          field={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function CustomFieldFormModal({
  field,
  onClose,
}: {
  field: CustomFieldDef | null;
  onClose: () => void;
}) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const [key, setKey] = useState(field?.key ?? '');
  const [label, setLabel] = useState(field?.label ?? '');
  const [type, setType] = useState<CustomFieldDef['type']>(field?.type ?? 'text');
  const [options, setOptions] = useState((field?.options ?? []).join('\n'));
  const [required, setRequired] = useState(field?.required ?? false);
  const mut = useMutation({
    mutationFn: async () => {
      const optList =
        type === 'enum'
          ? options
              .split('\n')
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
      if (field) {
        await updateCustomFieldDef(field.id, {
          label,
          type,
          options: optList,
          required,
          position: field.position,
        });
      } else {
        await createCustomFieldDef({ key, label, type, options: optList, required, position: 0 });
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['custom-fields'] });
      onClose();
    },
  });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[440px] rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[16px]">{field ? 'Editar campo' : 'Novo campo'}</h3>
          <button type="button" onClick={onClose} className="text-n-500" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>
        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Key (snake_case)</span>
            <input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              disabled={Boolean(field)}
              className="rounded-md border border-n-200 px-3 py-2 font-mono text-[13px] disabled:opacity-50"
              placeholder="cpf"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Label</span>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="rounded-md border border-n-200 px-3 py-2 text-[13px]"
              placeholder="CPF"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Tipo</span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as CustomFieldDef['type'])}
              className="rounded-md border border-n-200 px-3 py-2 text-[13px]"
            >
              <option value="text">Texto</option>
              <option value="number">Número</option>
              <option value="date">Data</option>
              <option value="boolean">Sim/Não</option>
              <option value="enum">Lista (enum)</option>
            </select>
          </label>
          {type === 'enum' && (
            <label className="flex flex-col gap-1">
              <span className="font-semibold text-[12px] text-n-700">Opções (uma por linha)</span>
              <textarea
                value={options}
                onChange={(e) => setOptions(e.target.value)}
                className="min-h-[100px] resize-y rounded-md border border-n-200 px-3 py-2 text-[13px]"
              />
            </label>
          )}
          <label className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
            />
            Obrigatório
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !label.trim() || !key.trim()}
          >
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}
